import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { 
  GraduationCap, Search, Filter, Eye, Calendar, MapPin, Users, 
  Phone, Mail, Building, Clock, CheckCircle, XCircle, AlertCircle,
  Trash2, MessageSquare, Send, Bell
} from 'lucide-react';
import { toast } from 'sonner';
import { CommunicationHistory } from '../../components/CommunicationHistory';

const API = '/api';

const STATUS_OPTIONS = [
  { value: 'new', label: 'New', labelAr: 'جديد', color: 'bg-blue-100 text-blue-800' },
  { value: 'contacted', label: 'Contacted', labelAr: 'تم التواصل', color: 'bg-yellow-100 text-yellow-800' },
  { value: 'qualified', label: 'Qualified', labelAr: 'مؤهل', color: 'bg-purple-100 text-purple-800' },
  { value: 'confirmed', label: 'Confirmed', labelAr: 'مؤكد', color: 'bg-green-100 text-green-800' },
  { value: 'closed', label: 'Closed', labelAr: 'مغلق', color: 'bg-gray-100 text-gray-800' },
  { value: 'cancelled', label: 'Cancelled', labelAr: 'ملغى', color: 'bg-red-100 text-red-800' },
];

export default function AdminEnrollmentRequestsPage() {
  const { language } = useLanguage();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [editForm, setEditForm] = useState({
    status: '',
    admin_notes: '',
    follow_up_date: ''
  });
  
  // Communication dialog state
  const [commDialogOpen, setCommDialogOpen] = useState(false);
  const [commType, setCommType] = useState('enrollment_follow_up');
  const [commMessage, setCommMessage] = useState('');
  const [commDeadline, setCommDeadline] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    fetchRequests();
  }, [statusFilter]);

  const fetchRequests = async () => {
    try {
      const url = statusFilter && statusFilter !== 'all' 
        ? `${API}/admin/enrollment-requests?status=${statusFilter}`
        : `${API}/admin/enrollment-requests`;
      
      const response = await fetch(url, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setRequests(data);
      }
    } catch (error) {
      console.error('Error fetching enrollment requests:', error);
      toast.error('Failed to load enrollment requests');
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = (request) => {
    setSelectedRequest(request);
    setEditForm({
      status: request.status,
      admin_notes: request.admin_notes || '',
      follow_up_date: request.follow_up_date || ''
    });
    setDetailDialogOpen(true);
  };

  const handleUpdateRequest = async () => {
    if (!selectedRequest) return;
    setUpdating(true);
    
    try {
      const response = await fetch(`${API}/admin/enrollment-requests/${selectedRequest.request_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(editForm)
      });
      
      if (response.ok) {
        toast.success('Enrollment request updated');
        setDetailDialogOpen(false);
        fetchRequests();
      } else {
        toast.error('Failed to update request');
      }
    } catch (error) {
      toast.error('Failed to update request');
    } finally {
      setUpdating(false);
    }
  };

  const handleDeleteRequest = async (requestId) => {
    if (!window.confirm('Are you sure you want to delete this enrollment request?')) return;
    
    try {
      const response = await fetch(`${API}/admin/enrollment-requests/${requestId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (response.ok) {
        toast.success('Enrollment request deleted');
        fetchRequests();
      } else {
        toast.error('Failed to delete request');
      }
    } catch (error) {
      toast.error('Failed to delete request');
    }
  };

  const handleSendCommunication = async () => {
    if (commType !== 'tna_reminder' && !commMessage.trim()) {
      toast.error('Please enter a message');
      return;
    }
    
    setSending(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/admin/enrollment-requests/${selectedRequest.request_id}/communicate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: commMessage,
          communication_type: commType,
          deadline: commType === 'tna_reminder' ? commDeadline : null,
        }),
      });

      if (response.ok) {
        const result = await response.json();
        toast.success(result.email_status === 'mocked' ? 'Communication logged (test mode)' : 'Communication sent successfully');
        setCommDialogOpen(false);
        setCommMessage('');
        setCommDeadline('');
        fetchRequests();
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Failed to send communication');
      }
    } catch (error) {
      toast.error('Failed to send communication');
    } finally {
      setSending(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusOption = STATUS_OPTIONS.find(s => s.value === status);
    if (!statusOption) return <Badge>{status}</Badge>;
    return (
      <Badge className={statusOption.color}>
        {language === 'ar' ? statusOption.labelAr : statusOption.label}
      </Badge>
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString(language === 'ar' ? 'ar-EG' : 'en-US', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  const filteredRequests = requests.filter(req => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      req.full_name?.toLowerCase().includes(term) ||
      req.email?.toLowerCase().includes(term) ||
      req.company?.toLowerCase().includes(term) ||
      req.course_title?.toLowerCase().includes(term) ||
      req.city?.toLowerCase().includes(term)
    );
  });

  // Stats
  const stats = {
    total: requests.length,
    new: requests.filter(r => r.status === 'new').length,
    contacted: requests.filter(r => r.status === 'contacted').length,
    confirmed: requests.filter(r => r.status === 'confirmed').length,
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#33AADD]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#111827]">
            {language === 'ar' ? 'طلبات التسجيل' : 'Enrollment Requests'}
          </h1>
          <p className="text-[#6B7280]">
            {language === 'ar' ? 'إدارة طلبات التسجيل في الدورات' : 'Manage course enrollment requests'}
          </p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#33AADD]/10 flex items-center justify-center">
                <GraduationCap className="h-5 w-5 text-[#33AADD]" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#111827]">{stats.total}</p>
                <p className="text-sm text-[#6B7280]">{language === 'ar' ? 'إجمالي' : 'Total'}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <AlertCircle className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#111827]">{stats.new}</p>
                <p className="text-sm text-[#6B7280]">{language === 'ar' ? 'جديد' : 'New'}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-yellow-100 flex items-center justify-center">
                <Phone className="h-5 w-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#111827]">{stats.contacted}</p>
                <p className="text-sm text-[#6B7280]">{language === 'ar' ? 'تم التواصل' : 'Contacted'}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                <CheckCircle className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#111827]">{stats.confirmed}</p>
                <p className="text-sm text-[#6B7280]">{language === 'ar' ? 'مؤكد' : 'Confirmed'}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#9CA3AF]" />
                <Input
                  placeholder={language === 'ar' ? 'بحث بالاسم، البريد، الشركة، الدورة...' : 'Search by name, email, company, course...'}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-[180px]">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder={language === 'ar' ? 'الحالة' : 'Status'} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{language === 'ar' ? 'جميع الحالات' : 'All Statuses'}</SelectItem>
                {STATUS_OPTIONS.map(status => (
                  <SelectItem key={status.value} value={status.value}>
                    {language === 'ar' ? status.labelAr : status.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Requests Table */}
      <Card>
        <CardContent className="p-0">
          {filteredRequests.length === 0 ? (
            <div className="text-center py-12">
              <GraduationCap className="h-12 w-12 text-[#D1D5DB] mx-auto mb-4" />
              <p className="text-[#6B7280]">
                {language === 'ar' ? 'لا توجد طلبات تسجيل' : 'No enrollment requests found'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b bg-[#F9FAFB]">
                    <th className="text-left p-4 font-medium text-[#6B7280]">{language === 'ar' ? 'الدورة' : 'Course'}</th>
                    <th className="text-left p-4 font-medium text-[#6B7280]">{language === 'ar' ? 'مقدم الطلب' : 'Applicant'}</th>
                    <th className="text-left p-4 font-medium text-[#6B7280]">{language === 'ar' ? 'التاريخ' : 'Dates'}</th>
                    <th className="text-left p-4 font-medium text-[#6B7280]">{language === 'ar' ? 'المشاركين' : 'Participants'}</th>
                    <th className="text-left p-4 font-medium text-[#6B7280]">{language === 'ar' ? 'الحالة' : 'Status'}</th>
                    <th className="text-left p-4 font-medium text-[#6B7280]">{language === 'ar' ? 'تاريخ الطلب' : 'Requested'}</th>
                    <th className="text-right p-4 font-medium text-[#6B7280]">{language === 'ar' ? 'إجراءات' : 'Actions'}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRequests.map((req) => (
                    <tr key={req.request_id} className="border-b hover:bg-[#F9FAFB]">
                      <td className="p-4">
                        <div>
                          <p className="font-medium text-[#111827] line-clamp-1">{req.course_title}</p>
                          <div className="flex items-center gap-2 text-sm text-[#6B7280]">
                            <MapPin className="h-3 w-3" />
                            <span>{req.city}</span>
                          </div>
                        </div>
                      </td>
                      <td className="p-4">
                        <div>
                          <p className="font-medium text-[#111827]">{req.full_name}</p>
                          <p className="text-sm text-[#6B7280]">{req.email}</p>
                          {req.company && (
                            <p className="text-sm text-[#9CA3AF]">{req.company}</p>
                          )}
                        </div>
                      </td>
                      <td className="p-4">
                        <div className="text-sm">
                          <p>{formatDate(req.start_date)}</p>
                          <p className="text-[#6B7280]">{formatDate(req.end_date)}</p>
                        </div>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-1">
                          <Users className="h-4 w-4 text-[#6B7280]" />
                          <span>{req.num_participants || 1}</span>
                        </div>
                      </td>
                      <td className="p-4">{getStatusBadge(req.status)}</td>
                      <td className="p-4 text-sm text-[#6B7280]">
                        {formatDate(req.created_at)}
                      </td>
                      <td className="p-4">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleViewDetails(req)}
                            data-testid={`view-request-${req.request_id}`}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteRequest(req.request_id)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {language === 'ar' ? 'تفاصيل طلب التسجيل' : 'Enrollment Request Details'}
            </DialogTitle>
          </DialogHeader>
          
          {selectedRequest && (
            <div className="space-y-6">
              {/* Course Info */}
              <div className="p-4 bg-[#F0F9FF] rounded-lg">
                <h3 className="font-semibold text-[#111827] mb-2">{selectedRequest.course_title}</h3>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-[#33AADD]" />
                    <span>{selectedRequest.city}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-[#33AADD]" />
                    <span>{formatDate(selectedRequest.start_date)} - {formatDate(selectedRequest.end_date)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4 text-[#33AADD]" />
                    <span>{selectedRequest.num_participants || 1} {language === 'ar' ? 'مشارك' : 'participant(s)'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-semibold">EUR {selectedRequest.price?.toLocaleString()}</span>
                  </div>
                </div>
              </div>

              {/* Applicant Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-[#6B7280]">{language === 'ar' ? 'الاسم' : 'Name'}</Label>
                  <p className="font-medium">{selectedRequest.full_name}</p>
                </div>
                <div>
                  <Label className="text-[#6B7280]">{language === 'ar' ? 'البريد' : 'Email'}</Label>
                  <p className="font-medium">{selectedRequest.email}</p>
                </div>
                <div>
                  <Label className="text-[#6B7280]">{language === 'ar' ? 'الهاتف' : 'Phone'}</Label>
                  <p className="font-medium">{selectedRequest.phone}</p>
                </div>
                <div>
                  <Label className="text-[#6B7280]">{language === 'ar' ? 'الشركة' : 'Company'}</Label>
                  <p className="font-medium">{selectedRequest.company || '-'}</p>
                </div>
              </div>

              {selectedRequest.notes && (
                <div>
                  <Label className="text-[#6B7280]">{language === 'ar' ? 'ملاحظات العميل' : 'Customer Notes'}</Label>
                  <p className="mt-1 p-3 bg-[#F9FAFB] rounded-lg text-sm">{selectedRequest.notes}</p>
                </div>
              )}

              {/* Admin Fields */}
              <div className="border-t pt-4 space-y-4">
                <h4 className="font-semibold text-[#111827]">{language === 'ar' ? 'إدارة الطلب' : 'Manage Request'}</h4>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{language === 'ar' ? 'الحالة' : 'Status'}</Label>
                    <Select value={editForm.status} onValueChange={(v) => setEditForm(prev => ({ ...prev, status: v }))}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {STATUS_OPTIONS.map(status => (
                          <SelectItem key={status.value} value={status.value}>
                            {language === 'ar' ? status.labelAr : status.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>{language === 'ar' ? 'تاريخ المتابعة' : 'Follow-up Date'}</Label>
                    <Input
                      type="date"
                      value={editForm.follow_up_date}
                      onChange={(e) => setEditForm(prev => ({ ...prev, follow_up_date: e.target.value }))}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>{language === 'ar' ? 'ملاحظات الإدارة' : 'Admin Notes'}</Label>
                  <Textarea
                    value={editForm.admin_notes}
                    onChange={(e) => setEditForm(prev => ({ ...prev, admin_notes: e.target.value }))}
                    rows={3}
                    placeholder={language === 'ar' ? 'ملاحظات داخلية...' : 'Internal notes...'}
                  />
                </div>
              </div>

              {/* Communication Section */}
              <div className="p-4 border rounded-lg bg-gradient-to-r from-blue-50 to-cyan-50">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-semibold text-[#111827] flex items-center gap-2">
                    <MessageSquare className="h-4 w-4 text-blue-600" />
                    {language === 'ar' ? 'التواصل' : 'Communication'}
                  </h4>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    onClick={() => { setCommType('enrollment_follow_up'); setCommDialogOpen(true); }}
                    data-testid="send-followup-btn"
                  >
                    <Mail className="h-4 w-4 mr-1" />
                    {language === 'ar' ? 'متابعة' : 'Follow-up'}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => { setCommType('tna_reminder'); setCommDialogOpen(true); }}
                    data-testid="send-tna-btn"
                  >
                    <Bell className="h-4 w-4 mr-1" />
                    {language === 'ar' ? 'تذكير TNA' : 'TNA Reminder'}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => { setCommType('training_confirmation'); setCommDialogOpen(true); }}
                    data-testid="send-confirmation-btn"
                  >
                    <CheckCircle className="h-4 w-4 mr-1" />
                    {language === 'ar' ? 'تأكيد' : 'Confirmation'}
                  </Button>
                </div>
              </div>

              {/* Communication History */}
              <CommunicationHistory
                recordType="enrollment"
                recordId={selectedRequest.request_id}
                title={language === 'ar' ? 'سجل التواصل' : 'Communication History'}
                maxHeight="200px"
              />

              <div className="text-sm text-[#6B7280]">
                <p>{language === 'ar' ? 'معرف الطلب:' : 'Request ID:'} {selectedRequest.request_id}</p>
                <p>{language === 'ar' ? 'تاريخ الطلب:' : 'Requested:'} {formatDate(selectedRequest.created_at)}</p>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setDetailDialogOpen(false)}>
              {language === 'ar' ? 'إغلاق' : 'Close'}
            </Button>
            <Button onClick={handleUpdateRequest} disabled={updating} className="btn-primary">
              {updating 
                ? (language === 'ar' ? 'جاري الحفظ...' : 'Saving...') 
                : (language === 'ar' ? 'حفظ التغييرات' : 'Save Changes')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Communication Dialog */}
      <Dialog open={commDialogOpen} onOpenChange={setCommDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {commType === 'tna_reminder' ? <Bell className="h-5 w-5" /> : <Mail className="h-5 w-5" />}
              {commType === 'enrollment_follow_up' && (language === 'ar' ? 'متابعة التسجيل' : 'Enrollment Follow-up')}
              {commType === 'tna_reminder' && (language === 'ar' ? 'تذكير TNA' : 'TNA Reminder')}
              {commType === 'training_confirmation' && (language === 'ar' ? 'تأكيد التدريب' : 'Training Confirmation')}
            </DialogTitle>
          </DialogHeader>
          {selectedRequest && (
            <div className="space-y-4">
              <div className="bg-gray-50 p-3 rounded-lg text-sm">
                <p><strong>{language === 'ar' ? 'إلى:' : 'To:'}</strong> {selectedRequest.full_name} &lt;{selectedRequest.email}&gt;</p>
                <p><strong>{language === 'ar' ? 'الدورة:' : 'Course:'}</strong> {selectedRequest.course_title}</p>
              </div>
              
              {commType === 'tna_reminder' && (
                <div>
                  <Label htmlFor="deadline">{language === 'ar' ? 'موعد التسليم' : 'Deadline'}</Label>
                  <Input
                    id="deadline"
                    type="date"
                    value={commDeadline}
                    onChange={(e) => setCommDeadline(e.target.value)}
                  />
                </div>
              )}
              
              <div>
                <Label htmlFor="commMessage">
                  {commType === 'tna_reminder' 
                    ? (language === 'ar' ? 'ملاحظات إضافية (اختياري)' : 'Additional Notes (Optional)')
                    : (language === 'ar' ? 'رسالتك' : 'Your Message')
                  }
                </Label>
                <Textarea
                  id="commMessage"
                  value={commMessage}
                  onChange={(e) => setCommMessage(e.target.value)}
                  placeholder={
                    commType === 'tna_reminder'
                      ? (language === 'ar' ? 'ملاحظات إضافية...' : 'Additional notes...')
                      : (language === 'ar' ? 'اكتب رسالتك هنا...' : 'Type your message here...')
                  }
                  rows={5}
                />
              </div>
              
              <p className="text-xs text-blue-600 bg-blue-50 p-2 rounded">
                {language === 'ar' 
                  ? 'البريد الإلكتروني في وضع الاختبار. يتم تسجيل الرسائل ولكن لا يتم إرسالها فعليًا حتى يتم تفعيل خدمة البريد.'
                  : 'Email is currently in test mode. Messages are logged but not actually sent until email service is activated.'
                }
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setCommDialogOpen(false)}>
              {language === 'ar' ? 'إلغاء' : 'Cancel'}
            </Button>
            <Button onClick={handleSendCommunication} disabled={sending}>
              {sending ? (language === 'ar' ? 'جاري الإرسال...' : 'Sending...') : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  {language === 'ar' ? 'إرسال' : 'Send'}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
